import * as geolib from 'geolib';
import { Structures } from "domains/structures/entities";
import { Space } from "generated/mos/structure";
import { Position2D } from "helpers/geo";
import mapboxgl from 'mapbox-gl';
import { DensityMapLayerStyle, DensityMapConfigurator } from './density-map';

export type Level = {
  id: string;
  name: string;
  spaces: Space.Entity[];
}

export type GeoJSONFeature = {
  id: string;
  type: 'Feature';
  properties: DensityMapLayerStyle & {
    name: string;
    height: number;
    baseHeight: number;
    maxHeight: number;
    color: string;
    area: number;
    level: number;
  };
  geometry: {
    type: 'Polygon';
    coordinates: Coordinate[][];
  };
};

export type GEOJSONFeatureCollection = {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
};

export type SiteFeatures = {
  siteId: string;
  levelNumbers: number[];
  name: string;
  bounds: mapboxgl.LngLatBounds;
  featureCollection: {
    type: 'FeatureCollection';
    features: GeoJSONFeature[];
  };
};

type FilteredSiteData = {
  id: string;
  name: string;
  boundary: {
    type: 'Polygon';
    coordinates: Coordinate[][];
  };
  levels: Level[];
}

type SiteDataLookupTable = {[siteId: string]: FilteredSiteData };
/**
 * Reshapes unsorted Space.Entity data into an object arranged by siteId and level* 
 * To render the map view, we need to get a collection of coordinates for each space on each level 
 * for the specific site being rendered (Getty Center or Getty Village).
 * This function reshapes the relevant info we need from structureData and spaceData into a
 * single object, so that values can be accessed in the form of `const geospatialData = siteId[levelNum]`
 * @param {Structures} structureData array of BuildingLevel entities, includes spaceRefs property, which lists id of each Space Entity on that level
 * @param {Space.Entity[]} spaceData array of Space Entity items. this is where all the geospatial coords for each space can be found.
 * @returns {SiteDataLookupTable}
 * @example const lookup = generateSiteLevelsLookup(structureData, spaceData); // generate the lookup table
 * @example lookup.abcdefg[0]  // get coordinates for all spaces on level 0 of site with id 'abcdefg';
 */
export const generateSiteLevelsLookup = (structureData: Structures, spaceData: readonly Space.Entity[], ignoreLevels: string[] = []): SiteDataLookupTable => {  

  const sites = structureData.sites.reduce((acc: any, site) => {
    if (!site.ref) return acc;
    const { boundary, name, ref: { id }} = site;
    return {...acc, [id]: {name, boundary, levels: []}}
  },{})
  
  structureData.buildingLevels.forEach((entity) => {
    
    // only include levels which have a siteRef and are not in the ignoreLevels list
    if (!entity.siteRef || !entity.ref || ignoreLevels.includes(entity.ref.id)) return;
    
    const siteId = entity.siteRef.id;
    
    // filter on a list of space ids to find all the space entity data for this level
    const spaceRefIdList = entity.spaceRefs && entity.spaceRefs.map(spaceRef => spaceRef.id) || [];
    const spaces = spaceData.filter(space => {
      return space.ref && space.ref.id && spaceRefIdList.includes(space.ref.id)
    });

    // don't include levels which have no entires in the filtered spaces list
    if (spaces.length ==0) return;
    
    // create the data object for this level
    const thisLevel: Level = { id: entity.ref.id, name: entity.name, spaces }
    sites[siteId].levels.push(thisLevel);
  });

  return sites;
}

type Coordinate = [number, number];

/**
 * Get the minimum and maximum x & y values required
 * for a bounding box which can fully contain a collection of
 * long/lat or x/y coordinates. The returned object is suitable for
 * passing to the maptalks getExtent() method.
 * @param shape Array of long/lat or x/y points
 * @returns Object {xmin:number, ymin: number, xmax: number, ymax: number}
 */
 export const getBounds = (shape: [number, number][]): any => {
  const extent: any = {};
  shape.forEach(([lat, long]) => {
    extent.xmin = extent.xmin ? Math.min(lat, extent.xmin) : lat;
    extent.xmax = extent.xmax ? Math.max(lat, extent.xmax) : lat;
    extent.ymin = extent.ymin ? Math.min(long, extent.ymin) : long;
    extent.ymax = extent.ymax ? Math.max(long, extent.ymax) : long;
  });
  return extent;
};

/**
 * Building levels aren't supplied with numerical data to indicate their
 * height above ground. This function makes a best guess based on the
 * level's name. Modifiers like 'Upper' or 'Terrace' will be ignored
 * EG: "Floor 1 Upper" will return <number> 1
 * @param levelName
 * @returns number
 */
export const guessFloorNum = (levelName: any) => {
  try {  
    const floorPtn = '(?:[fF]loor|[Ll]evel)';
    const numPtn = '([\\d]+)(?:rd|th|st){0,1}';
    const floorN = `${floorPtn}[ ]*${numPtn}`;
    const nFloor = `${numPtn}[ ]*${floorPtn}`;
    const matched = levelName.match(new RegExp(floorN)) ||levelName.match(new RegExp(nFloor)) || [0,0];
    return matched[1]
  } catch (e) {
    return 0;
  }
};

export const reshapePosition2D = (
  coords: Readonly<Position2D[] | undefined>,
  invertLatLong: boolean = false
): ReadonlyArray<[number, number]> => {
  if (!coords) return [];

  return coords.map(pair => {
    return invertLatLong ? [pair[1], pair[0]] : [pair[0], pair[1]];
  });
};

export const calculatePolygonArea = (points: ReadonlyArray<[number, number]>): number => {
  const coordinates = points.map(point => ({
    longitude: point[0],
    latitude: point[1]
  }));
  return Math.round(geolib.getAreaOfPolygon(coordinates));
};

/**
 * formats all of the spaces in the maoDate site
 * as polygons and property data suitable for
 * rendering on the map view.
 * @param mapData
 * @returns GeoJsonFeatureCollection
 */
export const formatSiteFeatures = (
  siteLookupData: SiteDataLookupTable,
  siteId: string,
  config: DensityMapConfigurator,
  stackLevels: boolean = false,
  ): SiteFeatures => {
  
  const {levels, boundary, name } = siteLookupData[siteId];

  const features: GeoJSONFeature[] =  [];
  const unsortedLevelNumbers: Set<number> = new Set();

  levels.forEach(level => {
    const { name, spaces } = level;
    const floorNum = guessFloorNum(name);
    const styleProps = config.getLayerStyle(floorNum);
    const [h, s, l] = styleProps.fillHSL;

    spaces.forEach(space => {
      if (!space.ref) return;

      const coords = reshapePosition2D(
        space.boundaries[0].polygon && space.boundaries[0].polygon.coordinates[0]
      );

      const area = calculatePolygonArea(coords);
      const withFloor = stackLevels ? floorNum : 0
      const { minHeight, maxHeight, base } = config.getYScale(withFloor);
      

      const featureJson: GeoJSONFeature = {
        type: 'Feature',
        id: space.ref.id,
        properties: {
          name: space.displayName,
          fillHSL: styleProps.fillHSL,
          floorColor: styleProps.floorColor,
          floorOpacity: styleProps.floorOpacity,
          color: `hsl(${h},${s}%,${l}%)`,
          baseHeight: base,
          height: minHeight,
          maxHeight: maxHeight,
          level: floorNum,
          area
        },
        geometry: {
          type: 'Polygon',
          coordinates: [[...coords]]
        }
      };
      features.push(featureJson);
    }, {});

    unsortedLevelNumbers.add(floorNum);
  });


  // Find the boundary of the current site by the coordinates of all spaces in all levels
  // We can't just use the site boundary values because the large distance between tram/arrival
  // area and main spaces in Getty center results in a tiny tiny map view.
  const hasExcludedLevels = config.excludeLevels && config.excludeLevels.length > 0;

  const boundaryCoords = hasExcludedLevels ? 
  features.reduce((acc: any, feature) => [...acc, ...feature.geometry.coordinates[0]],[])
  : boundary.coordinates[0];
  
  
  const bounds = boundaryCoords.reduce(function(bounds: any, coord: any) {
    return bounds.extend(coord);
  }, new mapboxgl.LngLatBounds(boundaryCoords[0], boundaryCoords[0])); 
  

  return {
    siteId,
    bounds,
    name,
    featureCollection: {
      type: 'FeatureCollection', 
      features,
    }, 
    levelNumbers: [...unsortedLevelNumbers].sort((a, b) => a - b)
  }
};
